import { useState, useCallback, useRef } from "react";
import { acceptServiceAgreement } from "@aspen/services";
import { ACCEPTED_LATEST_AGREEMENT, i18nCrossUtil } from "@aspen/libs";
import Bugsnag from "@bugsnag/js";

/**
 * 服务协议弹窗管理Hook
 *
 * 用于管理服务协议更新确认弹窗的状态和逻辑
 *
 * @returns {object} Hook返回值
 * @returns {boolean} visible - 弹窗显示状态
 * @returns {boolean} loading - API请求加载状态
 * @returns {function} showModal - 显示弹窗方法
 * @returns {function} handleAgree - 同意协议处理方法
 * @returns {function} handleClose - 关闭弹窗方法
 *
 * @example
 * ```tsx
 * const { visible, loading, showModal, handleAgree, handleClose } = useServiceAgreementModal();
 *
 * // 场景1: 登录成功后检查协议状态（需要回调继续登录流程）
 * if (data?.acceptedLatestAgreement === false) {
 *   showModal(() => {
 *     // 继续登录后的逻辑
 *   });
 * }
 *
 * // 场景2: 全局检查（不需要回调）
 * showModal(); // 用户同意后直接关闭弹窗
 *
 * // 渲染弹窗
 * <ModalServiceAgreement
 *   visible={visible}
 *   onClose={handleClose}
 *   onAgree={handleAgree}
 * />
 * ```
 */
export const useServiceAgreementModal = () => {
  const crossIntl = i18nCrossUtil.t();

  // 弹窗显示状态
  const [visible, setVisible] = useState(false);

  // API请求加载状态
  const [loading, setLoading] = useState(false);

  // 待执行的回调函数（用户同意协议后执行）
  // 使用 ref 而非 state，避免不必要的重渲染
  const callbackRef = useRef<(() => void) | null>(null);

  /**
   * 显示协议弹窗
   * @param callback - 可选，用户同意协议后执行的回调函数
   */
  const showModal = useCallback((callback?: () => void) => {
    setVisible(true);
    callbackRef.current = callback || null;
  }, []);

  /**
   * 处理用户同意协议
   * - 调用API确认协议
   * - 关闭弹窗
   * - 执行回调函数（继续登录流程）
   */
  const handleAgree = useCallback(async () => {
    setLoading(true);

    try {
      const res = await acceptServiceAgreement();

      // 只要调用了API，就继续执行登录流程，同时把本地存储的sessionStorage值改为true
      setVisible(false);
      sessionStorage.setItem(ACCEPTED_LATEST_AGREEMENT, "true");

      // 执行待处理的回调（继续登录流程）
      if (callbackRef.current) {
        callbackRef.current();
        callbackRef.current = null; // 执行后清空
      }
      if (res?.code !== "0") {
        Bugsnag.notify(new Error(`Accept service agreement error: ${res?.msg}`));
      }
    } catch (error) {
      // 网络错误或其他异常
      Bugsnag.notify(new Error("Accept service agreement error"));
    } finally {
      setLoading(false);
    }
  }, [crossIntl]);

  /**
   * 关闭弹窗
   * 注意：协议弹窗不允许直接关闭，用户必须同意协议
   * 此方法主要用于异常情况或取消操作（返回登录页）
   */
  const handleClose = useCallback(() => {
    // 可选：添加确认对话框
    // 或者直接返回登录页
    setVisible(false);
    callbackRef.current = null;
  }, []);

  return {
    visible,
    loading,
    showModal,
    handleAgree,
    handleClose,
  };
};
